import {
	useGetMyNotificationsLazyQuery,
	useGetMyUnreadNotificationCountQuery,
	useMarkAllNotificationsReadMutation,
	useMarkNotificationReadMutation,
	useOnNotificationAddedSubscription,
	useSubscriptionRestored,
	type GetMyNotificationsQuery,
} from '@codezo/graphql';
import { Bell, Mail } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, timeAgo } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

type NotificationItem = GetMyNotificationsQuery['getMyNotifications']['items'][number];

const PAGE_SIZE = 20;

/**
 * How often the badge re-checks itself. Long enough to be nearly free, short
 * enough that a missed live update doesn't sit wrong for a working session.
 */
const COUNT_POLL_MS = 60_000;

/** How long a locally-adjusted badge outranks a polled one. */
const COUNT_SETTLE_MS = 5_000;

/**
 * Notification bell.
 *
 * `title` and `body` are pre-rendered by the backend and shown as they arrive —
 * the wording is written at the moment the notification is raised, so historical
 * items keep the copy they were sent with and wording changes ship without a
 * frontend release. Nothing here is derived from `type`, which means a type this
 * build has never seen still renders correctly.
 *
 * Clicking routes through this app's `/n/:type/:id` resolver rather than working
 * out a destination here, so the bell and any other channel carrying a
 * `(type, id)` pair stay in step. Emailed links resolve in the client app, which
 * owns the public-facing copy of that route.
 */
export function NotificationBell() {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [items, setItems] = useState<NotificationItem[]>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [total, setTotal] = useState(0);
	const [unreadOnly, setUnreadOnly] = useState(false);

	// The badge is fetched on mount regardless of the subscription, and polled
	// after that. Both matter: sockets drop, and a dropped stream replays
	// nothing when it reconnects, so anything raised during the gap would never
	// arrive. Scheduled notifications made that real — they land at 09:00 on a
	// tab left open overnight, with no navigation or mutation to refetch on.
	// The count query is deliberately cheap; this is what it's for.
	const { data: countData, refetch: refetchCount } = useGetMyUnreadNotificationCountQuery({
		fetchPolicy: 'no-cache',
		pollInterval: COUNT_POLL_MS,
	});
	// The live stream was re-opened after a backend restart — catch up on the gap.
	useSubscriptionRestored(() => void refetchCount(), ['OnNotificationAdded']);

	/**
	 * Until when a polled count should be ignored. Marking read updates the badge
	 * locally, but a poll already in flight carries the count from before the
	 * mutation — landing after it, that would bounce the badge back up and leave
	 * it wrong until the next tick. Marking read is the bell's main interaction,
	 * so that's the most visible thing that could go wrong here.
	 */
	const ignoreCountUntil = useRef(0);

	useEffect(() => {
		if (Date.now() < ignoreCountUntil.current) return;
		if (typeof countData?.getMyUnreadNotificationCount === 'number') {
			setUnreadCount(countData.getMyUnreadNotificationCount);
		}
	}, [countData]);

	const [fetchNotifications, { loading }] = useGetMyNotificationsLazyQuery({
		fetchPolicy: 'no-cache',
	});
	const [markRead] = useMarkNotificationReadMutation();
	const [markAllRead] = useMarkAllNotificationsReadMutation();

	/**
	 * The response carries a fresh `unreadCount` too, so opening costs one trip.
	 * The filter is passed rather than read from state — the effect below has to
	 * reload when it flips, and a stale closure would fetch the old one.
	 */
	const load = useCallback(
		async (offset: number, onlyUnread: boolean) => {
			const result = await fetchNotifications({
				variables: { limit: PAGE_SIZE, offset, unreadOnly: onlyUnread },
			});
			const page = result.data?.getMyNotifications;
			if (!page) return;

			setItems((prev) => (offset === 0 ? page.items : [...prev, ...page.items]));
			// `total` counts the current filter, so it changes when the filter does
			// — which is exactly why paging restarts from 0 on a flip.
			setTotal(page.total);
			// `unreadCount` is across everything either way, so the badge is right
			// even while the list is filtered.
			setUnreadCount(page.unreadCount);
		},
		[fetchNotifications]
	);

	useEffect(() => {
		if (open) load(0, unreadOnly);
	}, [open, unreadOnly, load]);

	// An enhancement, not the source of truth — prepend and bump locally rather
	// than refetching. The id check guards a delivery that races an open.
	useOnNotificationAddedSubscription({
		onData: ({ data }) => {
			const added = data.data?.notificationAdded;
			if (!added) return;
			setItems((prev) => (prev.some((i) => i.id === added.id) ? prev : [added, ...prev]));
			setUnreadCount((count) => count + 1);
			setTotal((count) => count + 1);
		},
	});

	// Optimistic here, unlike the settings screen: both mutations are idempotent
	// and always report success, so there is no rejection to surface.
	const handleOpenNotification = (notification: NotificationItem) => {
		setOpen(false);

		if (!notification.readAt) {
			const readAt = new Date().toISOString();
			ignoreCountUntil.current = Date.now() + COUNT_SETTLE_MS;
			setItems((prev) => prev.map((i) => (i.id === notification.id ? { ...i, readAt } : i)));
			setUnreadCount((count) => Math.max(0, count - 1));
			markRead({ variables: { id: notification.id } }).catch(() => {
				// Idempotent and re-derivable on next open — nothing to tell the user.
			});
		}

		// Both null means informational: it is marked read, but links nowhere.
		if (notification.entityType && notification.entityId) {
			navigate(`/n/${notification.entityType}/${encodeURIComponent(notification.entityId)}`);
		}
	};

	const handleMarkAllRead = () => {
		ignoreCountUntil.current = Date.now() + COUNT_SETTLE_MS;
		if (unreadOnly) {
			// Nothing matches "unread" any more. Leaving the rows sitting there
			// marked read would contradict the filter the user is looking at.
			setItems([]);
			setTotal(0);
		} else {
			const readAt = new Date().toISOString();
			setItems((prev) => prev.map((i) => (i.readAt ? i : { ...i, readAt })));
		}
		setUnreadCount(0);
		markAllRead().catch(() => {});
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				// The visible heading gave way to the filter tabs, and the trigger is
				// icon-only — without this the control has no accessible name at all.
				aria-label={
					unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
				}
				className="relative flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground"
			>
				<Bell size={16} />
				{unreadCount > 0 && (
					<span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-2xs font-medium text-primary-foreground">
						{unreadCount > 9 ? '9+' : unreadCount}
					</span>
				)}
			</PopoverTrigger>
			<PopoverContent align="end" className="w-88 gap-0 p-0">
				<div className="flex items-center justify-between gap-2 border-b border-line-1 px-3 py-2.5">
					<div className="flex items-center gap-1">
						<FilterTab active={!unreadOnly} onClick={() => setUnreadOnly(false)}>
							All
						</FilterTab>
						<FilterTab active={unreadOnly} onClick={() => setUnreadOnly(true)}>
							Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}
						</FilterTab>
					</div>
					{unreadCount > 0 && (
						<button
							onClick={handleMarkAllRead}
							className="cursor-pointer text-xs text-text-3 transition-colors hover:text-text-1"
						>
							Mark all read
						</button>
					)}
				</div>

				<div className="max-h-96 overflow-y-auto">
					{loading && items.length === 0 ? (
						<div className="space-y-2 p-3">
							{Array.from({ length: 3 }).map((_, i) => (
								<div
									key={i}
									className="h-10 animate-pulse rounded bg-surface-fill"
								/>
							))}
						</div>
					) : items.length === 0 ? (
						// Empty is the normal state for a new user, not an edge case —
						// but under the filter it means something different, and
						// "nothing here yet" would read as though the bell were broken.
						<p className="px-3 py-10 text-center text-sm text-text-4">
							{unreadOnly ? 'Nothing unread' : 'Nothing here yet'}
						</p>
					) : (
						items.map((notification) => (
							<NotificationRow
								key={notification.id}
								notification={notification}
								onOpen={handleOpenNotification}
							/>
						))
					)}
				</div>

				{items.length < total && (
					<button
						onClick={() => load(items.length, unreadOnly)}
						disabled={loading}
						className="w-full cursor-pointer border-t border-line-1 py-2 text-xs text-text-3 transition-colors hover:text-text-1 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{loading ? 'Loading…' : 'Show older'}
					</button>
				)}
			</PopoverContent>
		</Popover>
	);
}

/**
 * Local rather than a shared primitive: the two apps' UI packages don't have a
 * matching chip between them, and this file is kept byte-identical in both.
 */
function FilterTab({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			onClick={onClick}
			className={cn(
				'cursor-pointer rounded-md px-2 py-0.5 text-xs transition-colors',
				active ? 'bg-surface-fill font-medium text-text-1' : 'text-text-3 hover:text-text-1'
			)}
		>
			{children}
		</button>
	);
}

function NotificationRow({
	notification,
	onOpen,
}: {
	notification: NotificationItem;
	onOpen: (notification: NotificationItem) => void;
}) {
	const unread = !notification.readAt;

	return (
		<button
			onClick={() => onOpen(notification)}
			className="flex w-full cursor-pointer gap-2.5 border-b border-line-1 px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-surface-hover"
		>
			<span
				aria-hidden
				className={`mt-1.5 size-1.5 shrink-0 rounded-full ${unread ? 'bg-primary' : 'bg-transparent'}`}
			/>
			<span className="min-w-0 flex-1">
				<span
					className={`block text-base ${unread ? 'font-medium text-text-1' : 'text-text-2'}`}
				>
					{notification.title}
				</span>
				{notification.body && (
					<span className="mt-0.5 block text-sm text-text-3">{notification.body}</span>
				)}
				<span className="mt-1 flex items-center gap-1.5 text-xs text-text-4">
					{timeAgo(notification.createdAt)}
					{/*
					 * Only ever rendered when true. False conflates an admin having
					 * switched email off for this type with no recipient resolving,
					 * an unknown template, and every send failing — so there is no
					 * honest "not emailed" to show. And true means the send didn't
					 * throw, not that it arrived: nothing tracks delivery.
					 */}
					{notification.emailSent && (
						<>
							<span aria-hidden>·</span>
							<span className="flex items-center gap-1">
								<Mail size={11} aria-hidden />
								Emailed
							</span>
						</>
					)}
				</span>
			</span>
		</button>
	);
}
